import React from "react";
import Link from "next/link";
import Meta from "../../components/Meta";
import { fetchApplicationsLight } from "../../lib/services/applicationsApiService";
import { SITE_URL } from "../../lib/siteUrl";
import {
  buildPlanningArchivePath,
  listPlanningArchiveCouncils,
  PLANNING_ARCHIVE_DEFAULT_COUNCIL,
  PLANNING_ARCHIVE_PAGE_SIZE,
  PLANNING_ARCHIVE_REVALIDATE_SECONDS,
  resolvePlanningArchiveRoute,
} from "../../lib/planningArchive";
import { COUNCILS, councilSlugToShortCode } from "../../lib/councils";
import { fetchBackendApplicationCounts } from "../../lib/backendStats";
import {
  buildPlanningApplicationPath,
  buildPlanningApplicationUrl,
} from "../../lib/planningApplicationUrl";

export const PLANNING_ARCHIVE_UNAVAILABLE_REVALIDATE_SECONDS = 30;

function formatDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getSortTimestamp(application) {
  const value =
    application?.scraped_date ||
    application?.validated_date ||
    application?.received_date ||
    application?.created_at;
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

function dedupeAndSortApplications(applications) {
  const items = Array.isArray(applications) ? applications : [];
  const byReference = new Map();

  items.forEach((application) => {
    const reference = String(application?.reference_no || application?.ref || "").trim();
    if (!reference) return;

    const key = reference.toUpperCase();
    if (!byReference.has(key)) {
      byReference.set(key, {
        ...application,
        reference_no: reference,
      });
    }
  });

  return [...byReference.values()].sort((left, right) => {
    const diff = getSortTimestamp(right) - getSortTimestamp(left);
    if (diff !== 0) return diff;
    return String(left.reference_no || "").localeCompare(String(right.reference_no || ""));
  });
}

function isDefaultCouncil(councilSlug) {
  return !councilSlug || councilSlug === PLANNING_ARCHIVE_DEFAULT_COUNCIL;
}

function getArchiveHeading(councilSlug, currentPage) {
  const council = COUNCILS[councilSlug];
  const base = isDefaultCouncil(councilSlug)
    ? "Planning Reference and Address Archive"
    : `${council?.displayName || councilSlug} Planning Applications Archive`;
  return currentPage <= 1 ? `${base} | City Scope` : `${base} | Page ${currentPage} | City Scope`;
}

function buildArchiveStructuredData(entries, currentPage, totalPages, councilSlug = PLANNING_ARCHIVE_DEFAULT_COUNCIL) {
  const pageUrl = `${SITE_URL}${buildPlanningArchivePath(currentPage, councilSlug)}`;
  const council = COUNCILS[councilSlug];
  const defaultCouncil = isDefaultCouncil(councilSlug);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#collection`,
        url: pageUrl,
        name: getArchiveHeading(councilSlug, currentPage),
        description: defaultCouncil
          ? "Plain-HTML archive of planning references and addresses for exact-match search and crawl discovery."
          : `Plain-HTML archive of every ${council?.displayName} planning application on City Scope, with its reference and address.`,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        mainEntity: { "@id": `${pageUrl}#itemlist` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Planning Archive",
            item: `${SITE_URL}/planning-archive`,
          },
          ...(defaultCouncil
            ? []
            : [
                {
                  "@type": "ListItem",
                  position: 3,
                  name: council?.displayName,
                  item: `${SITE_URL}${buildPlanningArchivePath(1, councilSlug)}`,
                },
              ]),
          ...(currentPage > 1
            ? [
                {
                  "@type": "ListItem",
                  position: defaultCouncil ? 3 : 4,
                  name: `Page ${currentPage}`,
                  item: pageUrl,
                },
              ]
            : []),
        ],
      },
      {
        "@type": "ItemList",
        "@id": `${pageUrl}#itemlist`,
        numberOfItems: entries.length,
        itemListOrder: "https://schema.org/ItemListOrderDescending",
        itemListElement: entries.map((entry, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: buildPlanningApplicationUrl(entry.reference_no, councilSlug),
          name: entry.address
            ? `${entry.reference_no} - ${entry.address}`
            : entry.reference_no,
        })),
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "City Scope",
        description:
          "Urban planning data visualization and analytics platform focused on Scotland.",
        ...(totalPages > 1 ? { hasPart: { "@id": `${pageUrl}#collection` } } : {}),
      },
    ],
  };
}

function buildPagination(currentPage, totalPages) {
  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  return [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);
}

export default function PlanningArchivePage({
  entries = [],
  currentPage = 1,
  totalPages = 1,
  dataUnavailable = false,
  councilSlug = PLANNING_ARCHIVE_DEFAULT_COUNCIL,
}) {
  const council = COUNCILS[councilSlug] || COUNCILS[PLANNING_ARCHIVE_DEFAULT_COUNCIL];
  const defaultCouncil = isDefaultCouncil(councilSlug);
  const pageTitle = getArchiveHeading(councilSlug, currentPage);
  const pageDescription = defaultCouncil
    ? "Browse a plain-HTML archive of planning references and addresses for exact-match search and fast crawl discovery."
    : `Browse every ${council.displayName} planning application on City Scope by reference and address. Page ${currentPage} of ${totalPages}.`;
  const canonicalPath = buildPlanningArchivePath(currentPage, councilSlug);
  const structuredData = buildArchiveStructuredData(entries, currentPage, totalPages, councilSlug);
  const pagination = buildPagination(currentPage, totalPages);
  const archiveCouncils = listPlanningArchiveCouncils();
  const hubPath = `/planning-updates/${councilSlugToShortCode(council.slug)}`;

  return (
    <>
      <Meta
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalPath}
        structuredData={dataUnavailable ? undefined : structuredData}
        noindex={dataUnavailable}
      />

      <main className="neo-civic-grid min-h-screen bg-[var(--c-bg)] text-[var(--c-text-primary)] px-4 py-10">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
          <section className="rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface)]/80 p-6 shadow-lg backdrop-blur-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--c-text-secondary)] font-['DM_Mono']">
              Planning archive
            </p>
            <h1 className="mt-2 text-3xl font-semibold font-['Space_Grotesk']">
              {defaultCouncil ? "Planning references and addresses" : `${council.displayName} planning applications`}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[var(--c-text-secondary)]">
              {defaultCouncil
                ? "A plain-HTML archive built for exact planning reference and address queries. Every row links directly to the canonical application page."
                : `Every ${council.displayName} planning application we hold, listed by reference and address. Each row links directly to the application page.`}
            </p>

            <div className="mt-5 flex flex-wrap gap-3 text-xs font-semibold font-['DM_Mono']">
              <Link
                href="/planning-updates"
                className="rounded-md border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1.5 text-[var(--c-accent)] hover:underline"
              >
                Planning Updates
              </Link>
              <Link
                href="/planning-map"
                className="rounded-md border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1.5 text-[var(--c-text-primary)] hover:underline"
              >
                Planning Map
              </Link>
              {defaultCouncil ? null : (
                <Link
                  href={hubPath}
                  className="rounded-md border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1.5 text-[var(--c-text-primary)] hover:underline"
                >
                  {council.displayName} latest applications
                </Link>
              )}
            </div>

            <nav aria-label="Planning archive by council" className="mt-5">
              <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--c-text-secondary)] font-['DM_Mono']">
                Archive by council
              </h2>
              <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-['DM_Mono']">
                {archiveCouncils.map((entry) => (
                  <li key={entry.slug}>
                    <Link
                      href={buildPlanningArchivePath(1, entry.slug)}
                      prefetch={false}
                      className={`hover:underline ${
                        entry.slug === council.slug
                          ? "font-semibold text-[var(--c-accent)]"
                          : "text-[var(--c-text-primary)]"
                      }`}
                      aria-current={entry.slug === council.slug ? "page" : undefined}
                    >
                      {entry.displayName}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </section>

          <section className="rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface)]/80 p-6 shadow-lg backdrop-blur-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--c-text-primary)] font-['Space_Grotesk']">
                  Archive page {currentPage} of {totalPages}
                </h2>
                <p className="mt-1 text-sm text-[var(--c-text-secondary)]">
                  Showing {entries.length.toLocaleString()} exact-match ref/address links on this page.
                </p>
              </div>
              <div className="text-xs text-[var(--c-text-secondary)] font-['DM_Mono']">
                Canonical path: {canonicalPath}
              </div>
            </div>

            <ol className="mt-5 space-y-3">
              {dataUnavailable ? (
                <li className="rounded-xl border border-[var(--c-border)] bg-[var(--c-bg)] p-4 text-sm text-[var(--c-text-secondary)]">
                  Archive data is temporarily unavailable. Please refresh shortly; the archive page itself is still available.
                </li>
              ) : null}
              {entries.map((entry) => {
                const reference = String(entry.reference_no || "").trim();
                const address = String(entry.address || "").trim();
                const category = String(entry.category || "").trim();
                const received = formatDate(entry.received_date);
                const validated = formatDate(entry.validated_date);
                const scraped = formatDate(entry.scraped_date);
                const href = buildPlanningApplicationPath(reference, councilSlug);

                return (
                  <li
                    key={reference}
                    className="rounded-xl border border-[var(--c-border)] bg-[var(--c-bg)] p-4"
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
                      <div className="min-w-0">
                        <Link
                          href={href}
                          prefetch={false}
                          className="font-['DM_Mono'] text-sm font-semibold text-[var(--c-accent)] hover:underline"
                        >
                          {reference}
                        </Link>
                        {address ? (
                          <div className="mt-1 text-sm text-[var(--c-text-primary)]">
                            {address}
                          </div>
                        ) : null}
                        {category ? (
                          <div className="mt-1 text-xs text-[var(--c-text-secondary)]">
                            Category: {category}
                          </div>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--c-text-secondary)] font-['DM_Mono']">
                        {received ? <span>Received: {received}</span> : null}
                        {validated ? <span>Validated: {validated}</span> : null}
                        {scraped ? <span>Updated: {scraped}</span> : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            {totalPages > 1 ? (
              <nav
                className="mt-6 flex flex-wrap items-center gap-2 text-xs font-semibold font-['DM_Mono']"
                aria-label="Planning archive pagination"
              >
                {currentPage > 1 ? (
                  <Link
                    href={buildPlanningArchivePath(currentPage - 1, councilSlug)}
                    className="rounded-md border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1.5 text-[var(--c-text-primary)] hover:underline"
                  >
                    Previous
                  </Link>
                ) : null}

                {pagination.map((page) => (
                  <Link
                    key={page}
                    href={buildPlanningArchivePath(page, councilSlug)}
                    className={`rounded-md border px-3 py-1.5 hover:underline ${
                      page === currentPage
                        ? "border-[var(--c-accent)] bg-[var(--c-accent)] text-[var(--c-bg)]"
                        : "border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text-primary)]"
                    }`}
                    aria-current={page === currentPage ? "page" : undefined}
                  >
                    {page}
                  </Link>
                ))}

                {currentPage < totalPages ? (
                  <Link
                    href={buildPlanningArchivePath(currentPage + 1, councilSlug)}
                    className="rounded-md border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1.5 text-[var(--c-text-primary)] hover:underline"
                  >
                    Next
                  </Link>
                ) : null}
              </nav>
            ) : null}
          </section>
        </div>
      </main>
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  const route = resolvePlanningArchiveRoute(params?.page);
  if (route.notFound) {
    return { notFound: true, revalidate: PLANNING_ARCHIVE_REVALIDATE_SECONDS };
  }
  if (route.redirect) {
    return { redirect: { destination: route.redirect, permanent: true } };
  }
  const councilSlug = route.council.slug;
  const currentPage = route.page;
  const offset = (currentPage - 1) * PLANNING_ARCHIVE_PAGE_SIZE;
  const [applicationResult, counts] = await Promise.all([
    fetchApplicationsLight({
      limit: PLANNING_ARCHIVE_PAGE_SIZE,
      offset,
      council: councilSlug,
      // This is a crawl surface, so fail fast into a temporary 200 state
      // instead of allowing a transient API response to become a page 500.
      retries: 0,
      timeoutMs: 10000,
      // Archive pages are server-rendered crawl surfaces. Give their bounded
      // read-model request the browser lane so normal archive refreshes are
      // not mistaken for the legacy Node fan-out lane and turned into 500s on
      // a 429.
      headers: {
        "User-Agent": "Mozilla/5.0 CityScopeArchiveProxy",
        "X-CityScope-Interactive": "archive",
      },
    })
      .then((data) => ({ data, unavailable: false }))
      .catch(() => ({ data: [], unavailable: true })),
    fetchBackendApplicationCounts({ timeoutMs: 3500 }).catch(() => null),
  ]);
  const applications = applicationResult.data;
  const sortedApplications = dedupeAndSortApplications(applications);
  const inferredTotalPages =
    sortedApplications.length === PLANNING_ARCHIVE_PAGE_SIZE
      ? currentPage + 1
      : currentPage;
  const councilApplicationCount =
    counts?.councilPlanningApplications?.[councilSlug];
  const totalPages = Math.max(
    1,
    councilApplicationCount
      ? Math.ceil(councilApplicationCount / PLANNING_ARCHIVE_PAGE_SIZE)
      : inferredTotalPages
  );

  if (sortedApplications.length === 0 && currentPage > 1) {
    if (currentPage > totalPages) {
      return {
        redirect: {
          destination: buildPlanningArchivePath(totalPages, councilSlug),
          permanent: true,
        },
      };
    }

    if (applicationResult.unavailable) {
      return {
        props: {
          entries: [],
          currentPage,
          totalPages,
          dataUnavailable: true,
          councilSlug,
        },
        // A transient origin timeout must not become a six-hour hard 404 for a
        // valid, sitemap-advertised archive page. Keep the route available but
        // noindexed, then retry quickly without replacing last-known-good ISR.
        revalidate: PLANNING_ARCHIVE_UNAVAILABLE_REVALIDATE_SECONDS,
      };
    }

    return {
      notFound: true,
      revalidate: PLANNING_ARCHIVE_REVALIDATE_SECONDS,
    };
  }

  return {
    props: {
      entries: sortedApplications,
      currentPage,
      totalPages,
      dataUnavailable: applicationResult.unavailable,
      councilSlug,
    },
    revalidate: applicationResult.unavailable
      ? 30
      : PLANNING_ARCHIVE_REVALIDATE_SECONDS,
  };
}
